import pictogramAsset from "assets/img/pictogram/asset.svg"
import iconLoadingUnloading from "assets/img/icon-loading-unloading.svg"

import { FC, ReactNode, useMemo } from "react"
import { useTranslation } from "react-i18next"

import Table, { TableRow } from "components/Table/Table"
import { Grid } from "components/Grid"
import Pictogram from "components/Pictogram/Pictogram"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"
import Lmid from "components/Lmid/Lmid"
import NoValue from "components/NoValue/NoValue"
import articleSidebars from "stores/articleSidebars"
import AssetStoredStatus from "components/AssetStoredStatus/AssetStoredStatus"
import RequestLocation from "models/RequestLocation"
import ShippingOrderAssetExtended from "models/ShippingOrderAssetExtended"
import { IShippingOrderAssetsSort } from "./config"
import useShippingOrderAssetsTable from "./hooks/useShippingOrderAssetsTable"
import ShippingOrderLocationItem from "./ShippingOrderLocationItem"

interface IShippingOrderModalPlannedAssetsProps {
    locations: RequestLocation[] | null
    sort: IShippingOrderAssetsSort | null
    setSort: (sort: IShippingOrderAssetsSort | null) => void
    assets: ShippingOrderAssetExtended[]
    isLoadingAssetDisplayStatuses: boolean
}

const ShippingOrderModalPlannedAssets: FC<IShippingOrderModalPlannedAssetsProps> = ({
    locations,
    sort,
    setSort,
    assets,
    isLoadingAssetDisplayStatuses,
}) => {
    const { t } = useTranslation()

    const {
        tableColumns,
    } = useShippingOrderAssetsTable({
        sort,
        setSort,
        isLoadingAssetDisplayStatuses,
    })

    const tableRows = useMemo<TableRow[]>(() => {
        return assets.filter(item => item.plannedAsset).map(item => {
            const columns: ReactNode[] = [
                <button type="button"
                    onClick={() => articleSidebars.show({ articleId: item.articleId })}
                    className="link d-block color-brand-blue">
                    <Grid columns="max-content minmax(0, auto)"
                        gap="1.6rem"
                        alignItems="center">
                        <Pictogram
                            src={pictogramAsset}
                            size="4rem"
                            imgSize="2.2rem"
                        />
                        <FlexibleContent text={item.plannedAsset?.altArticleDescription || item.plannedAsset?.articleDescription} />
                    </Grid>
                </button>,
                <Lmid
                    lmid={item.plannedAsset?.lmid}
                    assetId={item.plannedAsset?.assetId}
                    labeled={item.plannedAsset?.isLabeled}
                />,
                item.plannedAssetDisplayStatus
                    ? <Grid columns="max-content minmax(0, auto)" gap="1rem" alignItems="center">
                        <AssetStoredStatus status={item.plannedAssetDisplayStatus.status} />
                        <FlexibleContent text={item.plannedAssetDisplayStatus.locationName || ""} />
                    </Grid>
                    : <NoValue>{t("common.loading")}</NoValue>,
                <ShippingOrderLocationItem
                    locations={locations}
                    locationId={item.departureLocationId}
                    label={locations?.find(loc => loc.id === item.departureLocationId)?.description || ""}
                />,
                <img src={iconLoadingUnloading} alt="" />,
                <ShippingOrderLocationItem
                    locations={locations}
                    locationId={item.arrivalLocationId}
                    label={locations?.find(loc => loc.id === item.arrivalLocationId)?.description || ""}
                />,
                item.startDate?.format(t("config.dateLongFormat")) || <NoValue />,
                item.endDate?.format(t("config.dateLongFormat")) || <NoValue />,
            ]
            return columns
        })
    }, [assets, locations, t])

    return (
        <Table
            columns={tableColumns}
            rows={tableRows}
            theme="border-between"
            clampHeaders={2}
            stickyHeader
            condensed
        />
    )
}

export default ShippingOrderModalPlannedAssets
